import Vue from 'vue';
import Moment from 'moment-timezone';
import toastr from 'toastr';
import { i18n } from '@/plugins/i18n';
// Store utils
import rootStore from '@/store';
import { SEND_MESSAGE } from '@/store/modules/socket/action-types';
import * as types from './action-types';
import * as mutationTypes from './mutation-types';
// Utils
import { calert } from '@/utils/main';
import SocketMessages from '@/utils/classes/SocketMessages';
import { globalEventDispatcher, ShizenGlobalEvent } from '@/utils/event-dispatcher/global';
import { doAsync } from '@/utils/async';


/** Fetch and validate the meeting from local storage */
function getStoredMeeting(keyName) {
  let meeting = localStorage.getItem(keyName);
  if (meeting) {
    try {
      meeting = JSON.parse(meeting);
    } catch (e) {
      meeting = null;
    }
  }
  return meeting;
}


export default {

  /** Create and start a new meeting */
  async [types.START_MEETING](store, payload) {
    if (store.state.isMeetingInProgress) {
      // Prevent starting a meeting if another one is ongoing
      throw 0;
    }
    // Save meeting data to the server
    return Vue.axios({
      method: 'post',
      url: '/server/meetings/save.php',
      data: { meeting: payload.meeting, date: Moment().format('YYYY-MM-DD HH:mm:ss') }
    }).then((response) => {
      if (response.data instanceof Object) {
        const { data } = response;
        // Notify by socket
        rootStore.dispatch(`socket/${SEND_MESSAGE}`, {
          msg: SocketMessages.MT_MSGS.STARTED,
          id: response.data.id,
        });
        rootStore.dispatch(`socket/${SEND_MESSAGE}`, SocketMessages.NOTIF_MSG);
        // Setup meeting data
        data.steps = payload.meeting.steps;
        data.currentStep = 0;
        store.commit(mutationTypes.SET_MEETING_DATA, { meeting: data });
        localStorage.setItem(store.getters.storedMeetingKey(), JSON.stringify(data));
      } else {
        // Data format error
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
      }
    });
  },

  /** Prompt a dialog asking if the user want to join a meeting */
  [types.PROMPT_JOIN_MEETING](store, payload) {
    let alertMsg = i18n.t('message.MEETING_STARTED_JOIN');
    if (store.state.isMeetingInProgress) {
      if (Number(store.state.meetingData.id) === Number(payload.id)) {
        // Prevent to join the same meeting
        toastr.error(i18n.t('message.ERROR_SAME_MEETING'));
        return;
      }
      // Add a notice that the user will leave the current meeting
      alertMsg += `\r\n${i18n.t('message.NOTICE_LEAVE_MEETING')}`;
    }
    calert(alertMsg, [
      {
        title: i18n.t('message.JOIN_MEETING'),
        action: () => {
          // Leave current meeting before joining (if is on meeting)
          store.dispatch(types.LEAVE_MEETING, {
            callback: () => { store.dispatch(types.JOIN_MEETING, payload); }
          });
        },
      },
      {
        title: i18n.t('message.DONT_JOIN_MEETING'),
      },
    ]);
  },

  /** Join an already started meeting */
  async [types.JOIN_MEETING](store, payload) {
    if (store.state.isMeetingInProgress) {
      if (Number(store.state.meetingData.id) === Number(payload.id)) {
        // Prevent same meeting joining
        toastr.error(i18n.t('message.ERROR_SAME_MEETING'));
      } else {
        // Leave the current meeting and recall the function
        store.dispatch(types.LEAVE_MEETING, {
          callback: () => { store.dispatch(types.JOIN_MEETING, payload); }
        });
      }
      return;
    }
    // Get meeting data
    return Vue.axios({
      method: 'post',
      url: '/server/meetings/get.php',
      data: { ongoingId: payload.id },
    }).then((response) => {
      if (!response.data) throw 0;
      const { data } = response;
      if (data === -2) {
        // Meeting already ended
        toastr.error(i18n.t('message.MEETING_OVER'));
        // Check if it's a stored meeting and remove it
        const storedMeeting = getStoredMeeting(store.getters.storedMeetingKey());
        if (storedMeeting && Number(storedMeeting.id) === Number(payload.id)) {
          localStorage.removeItem(store.getters.storedMeetingKey());
        }
      } else if (data instanceof Object && data.id) {
        if (data.team !== rootStore.state.currentTeam.id) {
          // Prevent joining meeting that are not from the user's team
          console.error('Tried to join a meeting that is not from the user\'s team');
          throw 0;
        }
        // Setup meeting data
        store.commit(mutationTypes.SET_MEETING_DATA, { meeting: data, isMeetingSpectator: !payload.initiator });
      } else {
        // Data format error
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
      }
    });
  },

  /** Detect ongoing meeting in localStorage and start it */
  [types.RESUME_MEETING](store) {
    const ogMeeting = getStoredMeeting(store.getters.storedMeetingKey());
    if (!ogMeeting) {
      // No ongoing meeting to start
      return;
    }
    const creation = Moment(ogMeeting.created);
    if (creation.isValid() && Moment().diff(creation, 'minutes') > 15) {
      // Prompt dialog if meeting started a long time ago
      calert(
        i18n.t('message.MEETING_STARTED_RESUME', { name: ogMeeting.name }),
        [
          {
            title: i18n.t('message.RESUME'),
            action: () => {
              store.dispatch(types.JOIN_MEETING, { id: ogMeeting.id, initiator: true });
            },
          },
          {
            title: i18n.t('message.DISCARD_MEETING'),
            action: () => {
              store.dispatch(types.STOP_MEETING, {
                id: ogMeeting.id,
                callback: () => { localStorage.removeItem(store.getters.storedMeetingKey()); },
              });
            },
          },
        ],
      );
    } else {
      store.dispatch(types.JOIN_MEETING, { id: ogMeeting.id, initiator: true });
    }
  },

  /** Stop and end a meeting for everyone. If no meetingId given, stop the current meeting */
  async [types.STOP_MEETING](store, payload) {
    if (!payload.id) {
      // No meetingId parameter, want to stop current meeting
      if (!store.state.isMeetingInProgress) {
        // No meeting to stop
        if (payload.callback instanceof Function) {
          payload.callback();
        }
        return;
      }
      payload.id = store.state.meetingData.id;
    }
    // Send stop request to server
    return Vue.axios({
      method: 'post',
      url: '/server/meetings/end.php',
      data: { meetingId: payload.id, date: Moment().format('YYYY-MM-DD HH:mm:ss') },
    }).then((response) => {
      if (response.data === 1) {
        rootStore.dispatch(`socket/${SEND_MESSAGE}`, {
          msg: SocketMessages.MT_MSGS.END,
          id: payload.id,
        });
        toastr.success(i18n.t('message.MEETING_ENDED'));
        if (store.state.meetingData && Number(store.state.meetingData.id) === Number(payload.id)) {
          // Is current meeting, need to clear current meeting data
          localStorage.removeItem(store.getters.storedMeetingKey(store.state.meetingData.team));
          store.commit(mutationTypes.SET_MEETING_DATA, { meeting: null });
          globalEventDispatcher.dispatch(ShizenGlobalEvent.PUSH_HOME_PAGE);
        }
        if (payload.callback instanceof Function) {
          payload.callback();
        }
      } else {
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        if (payload.callback instanceof Function) {
          payload.callback();
        }
      }
    });
  },

  /* Leave the current meeting. End it if user isn't a spectator */
  [types.LEAVE_MEETING](store, payload) {
    if (!store.state.isMeetingInProgress) {
      // No meeting to leave
      if (payload && payload.callback instanceof Function) {
        payload.callback();
      }
      return;
    }
    if (!store.state.isMeetingSpectator) {
      // If not a spectator, meeting need to be stop or it will go indefinitely
      store.dispatch(types.STOP_MEETING, { id: null, ...payload });
    } else {
      store.commit(mutationTypes.SET_MEETING_DATA, { meeting: null });
      globalEventDispatcher.dispatch(ShizenGlobalEvent.PUSH_HOME_PAGE);
      if (payload && payload.callback instanceof Function) {
        payload.callback();
      }
    }
  },

  /** Fetch meeting plannings */
  async [types.FETCH_PLANNINGS](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/meetings/plannings/get.php',
        data: {},
      },
      mutationTypes: mutationTypes.GET_PLANNINGS,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then((response) => {
        if (response.data instanceof Array) {
          return response;
        }
        throw response;
      });
  },

  /** Create/Edit a planning */
  async [types.SET_PLANNING](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/meetings/plannings/set.php',
        data: payload,
      },
      mutationTypes: mutationTypes.GET_PLANNINGS,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then((response) => {
        if (response.data instanceof Array) {
          toastr.success(i18n.t('message.PLANNINGS_SAVED'));
          return response;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw response;
      });
  },

  /** Delete a meeting planning */
  async [types.DELETE_PLANNING](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/meetings/plannings/delete.php',
        data: payload,
      },
      mutationTypes: mutationTypes.GET_PLANNINGS,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then((response) => {
        if (response.data instanceof Array) {
          toastr.success(i18n.t('message.PLANNING_DELETED'));
          return response;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw response;
      });
  },

  /** Create/Edit a planning's step */
  async [types.SET_PLANNING_STEP](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/meetings/plannings/setStep.php',
        data: payload,
      },
      mutationTypes: mutationTypes.GET_PLANNINGS,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then((response) => {
        if (response.data instanceof Array) {
          toastr.success(i18n.t('message.PLANNINGS_SAVED'));
          return response;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw response;
      });
  },

  /** Delete a planning's step */
  async [types.DELETE_PLANNING_STEP](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/meetings/plannings/deleteStep.php',
        data: payload,
      },
      mutationTypes: mutationTypes.GET_PLANNINGS,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then((response) => {
        if (response.data instanceof Array) {
          toastr.success(i18n.t('message.PLANNINGS_SAVED'));
          return response;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw response;
      });
  },

};
